import { useCallback, useEffect, useRef, useState } from "react";
import {
  makeAccount,
  makeState,
  DEFAULT_SETTINGS,
  placeOrder as ePlaceOrder,
  cancelOrder as eCancelOrder,
  modifyOrder as eModifyOrder,
  closePosition as eClosePosition,
  onTick as eOnTick,
  setLeverage as eSetLeverage,
  switchAccount as eSwitchAccount,
  resetAccount as eResetAccount,
  updateSettings as eUpdateSettings,
  computeAccount,
  activeAccount,
} from "../lib/paper/engine";
import { loadState, clearState, loadActiveId, saveActiveId } from "../lib/paper/paperStore";
import {
  fetchManualState,
  createManualAccount,
  saveManualAccount,
  deleteManualAccount,
  saveManualSettings,
} from "../lib/paper/manualApi";

const SAVE_DELAY_MS = 800;
const POLL_MS = 15000;

// Keep the stored blob bounded (server caps an account at 2MB).
function compactForSave(acct) {
  const orders = Object.values(acct.orders);
  const live = orders.filter((o) => o.status === "working" || o.status === "inactive");
  const done = orders
    .filter((o) => o.status !== "working" && o.status !== "inactive")
    .sort((a, b) => (b.filledAt || b.placedAt || 0) - (a.filledAt || a.placedAt || 0))
    .slice(0, 500);
  return {
    ...acct,
    orders: Object.fromEntries([...live, ...done].map((o) => [o.id, o])),
    activity: acct.activity.slice(0, 500),
    trades: acct.trades.slice(0, 2000),
    balanceHistory: acct.balanceHistory.slice(-2000),
  };
}

// React binding for the pure engine. Accounts are loaded from and saved to the
// backend (per-account, versioned); resting orders + liquidation re-evaluate
// whenever live quotes move. `quotes` is the map from useOkxTickers.
export function usePaperEngine(quotes) {
  const [state, setState] = useState(null); // null until the server answers
  const [status, setStatus] = useState({ loading: true, error: null, conflict: null });
  const quotesRef = useRef(quotes);
  quotesRef.current = quotes;
  const stateRef = useRef(state);
  stateRef.current = state;

  // Per-account sync bookkeeping: { [id]: { version, json } } — json is the
  // last payload the server holds, so unchanged ticks never trigger a PUT.
  const synced = useRef({});
  const saving = useRef(false);

  const adoptServer = useCallback((server) => {
    const accounts = {};
    for (const row of server.accounts) {
      const acct = row.account;
      accounts[acct.id] = acct;
      synced.current[acct.id] = { version: row.version, json: JSON.stringify(compactForSave(acct)) };
    }
    return accounts;
  }, []);

  // ── initial load (+ one-time migration of browser-only accounts) ──
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        let server = await fetchManualState();
        if (!server.accounts.length) {
          const local = loadState();
          const seed = local?.accounts && Object.keys(local.accounts).length
            ? Object.values(local.accounts)
            : [makeAccount({ name: "Paper 1" })];
          for (const acct of seed) await createManualAccount(compactForSave(acct));
          if (local?.settings) await saveManualSettings(local.settings);
          server = await fetchManualState();
        }
        clearState();
        if (!live) return;
        const accounts = adoptServer(server);
        const ids = Object.keys(accounts);
        const saved = loadActiveId();
        setState({
          ...makeState(),
          settings: { ...DEFAULT_SETTINGS, ...server.settings },
          accounts,
          activeAccountId: ids.includes(saved) ? saved : ids[0],
        });
        setStatus({ loading: false, error: null, conflict: null });
      } catch (e) {
        if (live) setStatus({ loading: false, error: e?.message || "Couldn't load paper accounts", conflict: null });
      }
    })();
    return () => { live = false; };
  }, [adoptServer]);

  useEffect(() => {
    if (state?.activeAccountId) saveActiveId(state.activeAccountId);
  }, [state?.activeAccountId]);

  // ── debounced save of any account whose content changed ──
  const flush = useCallback(async () => {
    const s = stateRef.current;
    if (!s) return;
    if (saving.current) { setTimeout(() => flushRef.current(), SAVE_DELAY_MS); return; }
    saving.current = true;
    try {
      for (const acct of Object.values(s.accounts)) {
        const payload = compactForSave(acct);
        const json = JSON.stringify(payload);
        const meta = synced.current[acct.id];
        if (!meta || meta.json === json) continue;
        const res = await saveManualAccount(payload, meta.version);
        if (res.ok) {
          synced.current[acct.id] = { version: res.version, json };
        } else if (res.conflict) {
          // Another device saved first — its copy wins; drop our stale edit.
          const cur = res.conflict;
          synced.current[acct.id] = { version: cur.version, json: JSON.stringify(compactForSave(cur.account)) };
          setState((st) => st && { ...st, accounts: { ...st.accounts, [acct.id]: cur.account } });
          setStatus((x) => ({ ...x, conflict: `“${cur.account.name}” was changed on another device — reloaded its latest state.` }));
        } else if (res.missing) {
          delete synced.current[acct.id];
          setState((st) => {
            if (!st) return st;
            const { [acct.id]: _gone, ...rest } = st.accounts;
            const ids = Object.keys(rest);
            if (!ids.length) return st;
            return { ...st, accounts: rest, activeAccountId: st.activeAccountId === acct.id ? ids[0] : st.activeAccountId };
          });
        }
      }
      setStatus((x) => (x.error ? { ...x, error: null } : x));
    } catch (e) {
      setStatus((x) => ({ ...x, error: `Not saved — ${e?.message || "server unreachable"}. Retrying…` }));
      setTimeout(() => flushRef.current(), 5000);
    } finally {
      saving.current = false;
    }
  }, []);

  const flushRef = useRef(flush);
  flushRef.current = flush;

  // Throttled, not debounced: quote ticks rewrite state continuously, so a
  // debounce could postpone the save forever. Retries ride the next change.
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!state || saveTimer.current) return;
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      flush();
    }, SAVE_DELAY_MS);
  }, [state, flush]);
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    flush();
  }, [flush]);

  // ── pick up changes made on other devices ──
  useEffect(() => {
    if (status.loading) return undefined;
    const poll = async () => {
      if (document.hidden || saving.current || !stateRef.current) return;
      try {
        const server = await fetchManualState();
        setState((st) => {
          if (!st) return st;
          const accounts = { ...st.accounts };
          let changed = false;
          const serverIds = new Set();
          for (const row of server.accounts) {
            const id = row.account.id;
            serverIds.add(id);
            const meta = synced.current[id];
            const localJson = accounts[id] ? JSON.stringify(compactForSave(accounts[id])) : null;
            const localDirty = meta && localJson !== meta.json;
            if (!meta || (row.version > meta.version && !localDirty)) {
              accounts[id] = row.account;
              synced.current[id] = { version: row.version, json: JSON.stringify(compactForSave(row.account)) };
              changed = true;
            }
          }
          for (const id of Object.keys(accounts)) {
            if (!serverIds.has(id) && synced.current[id]) {
              delete accounts[id];
              delete synced.current[id];
              changed = true;
            }
          }
          const ids = Object.keys(accounts);
          if (!changed || !ids.length) return st;
          return { ...st, accounts, activeAccountId: accounts[st.activeAccountId] ? st.activeAccountId : ids[0] };
        });
      } catch { /* transient — next poll retries */ }
    };
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [status.loading]);

  // evaluate resting orders + liquidation on each throttled quote update.
  useEffect(() => {
    if (!quotes || !Object.keys(quotes).length) return;
    setState((s) => (s ? eOnTick(s, quotes) : s));
  }, [quotes]);

  const withQuotes = () => quotesRef.current || {};

  const placeOrder = useCallback((spec) => {
    let result;
    setState((s) => {
      result = ePlaceOrder(s, spec, withQuotes());
      return result.state;
    });
    return result; // { order, error } — note: state here is pre-commit snapshot
  }, []);

  const cancelOrder = useCallback((id) => {
    setState((s) => eCancelOrder(s, id).state);
  }, []);

  const modifyOrder = useCallback((id, patch) => {
    setState((s) => eModifyOrder(s, id, patch).state);
  }, []);

  const closePosition = useCallback((symbol, opts) => {
    setState((s) => eClosePosition(s, symbol, withQuotes(), opts).state);
  }, []);

  const setLeverage = useCallback((symbol, lev) => setState((s) => eSetLeverage(s, symbol, lev)), []);
  const switchAccount = useCallback((id) => setState((s) => eSwitchAccount(s, id)), []);
  const resetAccount = useCallback((id, opts) => setState((s) => eResetAccount(s, id, opts)), []);

  const updateSettings = useCallback((patch) => {
    setState((s) => {
      const next = eUpdateSettings(s, patch);
      saveManualSettings(next.settings).catch(() =>
        setStatus((x) => ({ ...x, error: "Settings not saved — server unreachable." })));
      return next;
    });
  }, []);

  // Created on the server first so the account exists everywhere before use.
  const addAccount = useCallback(async ({ name, startingBalance } = {}) => {
    const acct = makeAccount({ name, startingBalance });
    const row = await createManualAccount(acct);
    synced.current[acct.id] = { version: row.version, json: JSON.stringify(compactForSave(row.account)) };
    setState((s) => ({ ...s, accounts: { ...s.accounts, [acct.id]: row.account }, activeAccountId: acct.id }));
    return row.account;
  }, []);

  const deleteAccount = useCallback(async (id) => {
    const s = stateRef.current;
    if (!s || Object.keys(s.accounts).length <= 1) throw new Error("Keep at least one account.");
    await deleteManualAccount(id);
    delete synced.current[id];
    setState((st) => {
      const { [id]: _gone, ...rest } = st.accounts;
      return { ...st, accounts: rest, activeAccountId: st.activeAccountId === id ? Object.keys(rest)[0] : st.activeAccountId };
    });
  }, []);

  const dismissConflict = useCallback(() => setStatus((x) => ({ ...x, conflict: null })), []);

  const account = state ? activeAccount(state) : null;
  const lastPrices = {};
  for (const k of Object.keys(quotes || {})) lastPrices[k] = quotes[k].last;
  const rollup = account ? computeAccount(account, lastPrices, state.settings) : null;

  return {
    ready: !!state,
    status,
    state,
    account,
    rollup,
    settings: state?.settings || DEFAULT_SETTINGS,
    lastPrices,
    actions: {
      placeOrder, cancelOrder, modifyOrder, closePosition, setLeverage,
      addAccount, deleteAccount, switchAccount, resetAccount, updateSettings, dismissConflict,
    },
  };
}

export default usePaperEngine;
